import "./ArtistModal.scss";

import classNames from "classnames";
import { Button, Form, Modal } from "react-bootstrap";
import LocationInput from "../../../components/Form/LocationInput";
import { useForm } from "../../../hooks/Form";
import { useArtistContext } from "../ArtistPageContext";

const GENRES = [
  "Trap",
  "Trance",
  "Roots",
  "Rock",
  "Reggae",
  "R&B/Soul",
  "Rap",
  "Punk",
  "Pop",
  "New Age",
  "Metal",
  "Latin",
  "Jazz",
  "Indie",
  "Hip-hop",
  "Gospel",
  "Electronic",
  "Disco",
  "Dance",
  "Country",
  "Contemporary Blues",
  "Classical",
  "Blues",
  "Alternative",
];

const INSTRUMENTS = [
  "Vocals",           // vocalist: 53
  "Electric Guitar",  // guitar: 28
  "Acoustic Guitar",  // guitar: 28
  "Keys",             // keys: 21
  "Violin",           // strings: 19
  "Cello",            // strings: 19
  "Drums",            // drum: 17
  "Winds",            // winds: 17
  "Horns",            // winds: 17 
  "Bass",             // bass: 8
  "Beats",            // producer: 5
  "DJ",               // producer: 5
  "Accordion",        // accordion: 3
  "Percussion",       // percussion: 3
];

function createInitialValues(artist) {
  return {
    name: artist.name,
    address: artist.address,
    latitude: artist.latitude,
    longitude: artist.longitude,
    bio: artist.bio,
    genres: artist.genres,
    instruments: artist.instruments,
  };
}

export default function PersonalInfoModal({ show, handleClose, onSave }) {
  const { artist, setArtist } = useArtistContext();
  const { values, setValues, setFieldValue, handleChange } = useForm({ initialValues: createInitialValues(artist) });

  const clearLocationValues = () => {
    setValues((oldValues) => ({
      ...oldValues,
      address: null,
      latitude: null,
      longitude: null,
    }));
  }

  const handleLocationInputClear = () => {
    clearLocationValues();
  };

  const handleLocationPlaceChange = (place) => {
    if (!place || !place.address || !place.latitude || !place.longitude) {
      return clearLocationValues();
    }

    setValues((oldValues) => ({
      ...oldValues,
      address: place.address,
      latitude: place.latitude,
      longitude: place.longitude,
    }));
  };

  const handleSave = async () => {
    const updatedData = {
      name:        values.name,
      address:     values.address,
      latitude:    values.latitude,
      longitude:   values.longitude,
      bio:         values.bio,
      genres:      values.genres,
      instruments: values.instruments
    };

    setArtist(prev => ({ ...prev, ...updatedData }));

    onSave && onSave(updatedData);

    handleClose();
  };

  const toggleGenre = (genre) => {
    const oldGenres = values.genres;

    if (oldGenres.includes(genre)) {
      return setFieldValue("genres", oldGenres.filter((g) => g !== genre));
    }

    if (oldGenres.length >= 3) {
      return;
    }

    setFieldValue("genres", [...oldGenres, genre]);
  };

  const toggleInstrument = (instrument) => {
    const oldInstruments = values.instruments;

    if (oldInstruments.includes(instrument)) {
      return setFieldValue("instruments", oldInstruments.filter((i) => i !== instrument));
    }

    setFieldValue("instruments", [...oldInstruments, instrument]);
  };

  return (
    <Modal className="__artist-modal" show={ show } onHide={ handleClose } centered>
      <Modal.Header closeButton>
        <Modal.Title>Update Your Personal Info</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className="modal-subtitle">
          Let everyone know a little bit about yourself!
        </div>
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>Name</Form.Label>
            <Form.Control
              type="text"
              name="name"
              placeholder="Enter your name"
              value={ values.name ?? "" }
              onChange={ handleChange }
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Location</Form.Label>
            <LocationInput
              name="address"
              placeholder="Enter your location"
              value={ values.address ?? "" }
              onInputClear={ handleLocationInputClear }
              onPlaceChange={ handleLocationPlaceChange }
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Bio</Form.Label>
            <Form.Control
              as="textarea"
              name="bio"
              placeholder="Tell us about yourself"
              rows={ 4 }
              value={ values.bio ?? "" }
              onChange={ handleChange }
            />
          </Form.Group>

          <Form.Label>Choose up to 3 genres that represent your style</Form.Label>
          <div className="genre-grid">
            { GENRES.map((genre) => (
              <div
                key={ genre }
                className={ classNames("genre", { "selected": values.genres.includes(genre) }) }
                onClick={ () => toggleGenre(genre) }
              >
                { genre }
              </div>
            )) }
          </div>

          <Form.Label>Choose up to 3 instruments</Form.Label>
          <div className="genre-grid">
            { INSTRUMENTS.map((instrument) => (
              <div
                key={ instrument }
                className={ classNames("genre", { "selected": values.instruments.includes(instrument) }) }
                onClick={ () => toggleInstrument(instrument) }
              >
                { instrument }
              </div>
            )) }
          </div>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button className="ppy-btn ppy-btn-tertiary" onClick={ handleClose }>Cancel</Button>
        <Button className="ppy-btn ppy-btn-primary" onClick={ handleSave }>Save</Button>
      </Modal.Footer>
    </Modal>
  );
}
